import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, OnDestroy, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AgGridModule } from 'ag-grid-angular';
import { GridReadyEvent, GridApi, ChartModel, ColumnState } from 'ag-grid-community';
import { UIIconsComponent } from '../../ui/icons/icons.component';
import { AgGridChartComponent } from '../ag-grid-chart/ag-grid-chart.component';

export interface TableData {
  headers: any[];
  rows: any[];
}

@Component({
  selector: 'app-data-visualization',
  standalone: true,
  imports: [CommonModule, FormsModule, AgGridModule, UIIconsComponent, AgGridChartComponent],
  templateUrl: './data-visualization.component.html',
  styleUrls: ['./data-visualization.component.css']
})
export class DataVisualizationComponent implements OnInit, OnChanges, OnDestroy {
  @Input() data: any[] = [];
  @Input() tableData: TableData | null = null;
  @Input() viewMode: 'grid' | 'chart' = 'grid';
  @Input() showToggle: boolean = true;
  @Input() canvasId: string = 'dataVisualizationCanvas';
  @Input() isCanvas: boolean = false;
  @Input() chartModel: ChartModel | null = null;
  @Input() agGridChartType: string = 'bar';
  @Input() defaultColDef: any = {
    flex: 1,
    width: 100,
    resizable: true,
    filter: true,
    sortable: true
  };

  @Input() filterModel: any = null;
  @Input() sortModel: ColumnState[] = [];

  @Output() viewModeChange = new EventEmitter<'grid' | 'chart'>();
  @Output() chartModelChange = new EventEmitter<ChartModel>();
  @Output() filterChange = new EventEmitter<any>();
  @Output() sortChange = new EventEmitter<ColumnState[]>();

  @ViewChild(AgGridChartComponent) agGridChartComponent?: AgGridChartComponent;

  public columnDefs: any[] = [];
  public rowData: any[] = [];
  public filteredRowData: any[] = [];
  public gridApi!: GridApi;
  public hiddenGridApi!: GridApi;
  private pendingFilterSort = false;
  public lastKnownChartModel: ChartModel | null = null;

  public canvasGridOptions = {
    suppressCellFocus: true,
    suppressRowHoverHighlight: false
  };

  public hiddenGridOptions: any = {
    enableCharts: true,
    enableRangeSelection: true,
    chartThemes: ['ag-default'],
    popupParent: document.body,
    chartMenuItems: (params: any) => {
      return params.defaultItems.filter(
        (item: any) => item !== 'chartUnlink' && item !== 'chartLink'
      );
    }
  };

  constructor() {}

  ngOnInit(): void {
    this.initializeData();
    if (this.chartModel) {
      this.lastKnownChartModel = this.chartModel;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] || changes['tableData']) {
      this.initializeData();
    }

    const filterChanged = changes['filterModel'];
    const sortChanged = changes['sortModel'];

    if (filterChanged || sortChanged) {
      if (this.gridApi) {
        if (filterChanged && this.filterModel) {
          this.restoreFilterModel();
        }
        if (sortChanged && this.sortModel?.length > 0) {
          this.restoreSortModel();
        }
      } else {
        this.applyFilterAndSortViaHiddenGrid();
      }
    }

    if (changes['viewMode'] && this.viewMode === 'chart' && !this.gridApi) {
      if (this.filterModel || this.sortModel?.length > 0) {
        this.applyFilterAndSortViaHiddenGrid();
      }
    }

    if (changes['viewMode'] && !this.isCanvas) {
      queueMicrotask(() => {
        if (this.viewMode === 'grid' && this.gridApi) {
          try {
            this.gridApi.sizeColumnsToFit();
          } catch {
            /* layout not ready */
          }
        }
      });
    }
  }

  private initializeData(): void {
    if (this.tableData) {
      this.columnDefs = this.tableData.headers;
      this.rowData = this.tableData.rows;
      this.filteredRowData = [...this.tableData.rows];
    } else if (this.data && this.data.length > 0) {
      this.generateTableFromData();
      this.filteredRowData = [...this.rowData];
    }

    if (this.viewMode === 'chart' && (this.filterModel || this.sortModel?.length > 0)) {
      this.applyFilterAndSortViaHiddenGrid();
    }
  }

  private generateTableFromData(): void {
    if (!this.data || this.data.length === 0) return;

    const keys = Object.keys(this.data[0]);
    this.columnDefs = keys.map(key => ({
      headerName: key,
      field: key,
      filter: 'agTextColumnFilter',
      sortable: true,
      ...(this.isCanvas && {
        minWidth: 120,
        resizable: true,
        width: 140
      })
    }));
    this.rowData = this.data;
  }

  public toggleView(): void {
    const newMode = this.viewMode === 'grid' ? 'chart' : 'grid';

    if (newMode === 'chart' && this.gridApi) {
      this.syncVisibleGridToHiddenGrid();
    }

    this.viewMode = newMode;
    this.viewModeChange.emit(newMode);
  }

  private syncVisibleGridToHiddenGrid(): void {
    if (!this.gridApi || !this.hiddenGridApi) return;

    const currentFilterModel = this.gridApi.getFilterModel();
    this.hiddenGridApi.setFilterModel(currentFilterModel);

    const columnState = this.gridApi.getColumnState();
    const sortState = columnState.filter(col => col.sort != null);
    if (sortState.length > 0) {
      this.hiddenGridApi.applyColumnState({
        state: sortState,
        applyOrder: false
      });
    } else {
      this.hiddenGridApi.applyColumnState({ defaultState: { sort: null } });
    }

    this.extractFilteredDataFromHiddenGrid();
  }

  public onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;

    if (this.filterModel && Object.keys(this.filterModel).length > 0) {
      this.restoreFilterModel();
    }

    if (this.sortModel && this.sortModel.length > 0) {
      this.restoreSortModel();
    }

    if (!this.isCanvas) {
      queueMicrotask(() => {
        try {
          params.api.sizeColumnsToFit();
        } catch {
          /* noop */
        }
      });
    }
  }

  public onHiddenGridReady(params: GridReadyEvent): void {
    this.hiddenGridApi = params.api;

    if (this.pendingFilterSort) {
      this.pendingFilterSort = false;
      this.applyFilterAndSortViaHiddenGrid();
    } else if (this.filterModel || this.sortModel?.length > 0) {
      this.applyFilterAndSortViaHiddenGrid();
    } else {
      this.extractFilteredDataFromHiddenGrid();
    }
  }

  public onChartModelChange(chartModel: ChartModel): void {
    this.lastKnownChartModel = chartModel;
    this.chartModelChange.emit(chartModel);
  }

  public onFilterChanged(): void {
    if (this.gridApi) {
      const currentFilterModel = this.gridApi.getFilterModel();
      this.filterChange.emit(currentFilterModel);

      if (this.hiddenGridApi) {
        this.hiddenGridApi.setFilterModel(currentFilterModel);
        this.extractFilteredDataFromHiddenGrid();
      }
    }
  }

  public onSortChanged(): void {
    if (this.gridApi) {
      const columnState = this.gridApi.getColumnState();
      const sortState = columnState.filter(col => col.sort != null);
      this.sortChange.emit(sortState);

      if (this.hiddenGridApi) {
        if (sortState.length > 0) {
          this.hiddenGridApi.applyColumnState({
            state: sortState,
            applyOrder: false
          });
        } else {
          this.hiddenGridApi.applyColumnState({ defaultState: { sort: null } });
        }
        this.extractFilteredDataFromHiddenGrid();
      }
    }
  }

  private restoreFilterModel(): void {
    if (this.gridApi && this.filterModel) {
      try {
        this.gridApi.setFilterModel(this.filterModel);
        if (this.hiddenGridApi) {
          this.hiddenGridApi.setFilterModel(this.filterModel);
          setTimeout(() => this.extractFilteredDataFromHiddenGrid(), 0);
        }
      } catch (error) {
        console.warn('Failed to restore filter model:', error);
      }
    }
  }

  private restoreSortModel(): void {
    if (this.gridApi && this.sortModel && this.sortModel.length > 0) {
      try {
        this.gridApi.applyColumnState({
          state: this.sortModel,
          applyOrder: false
        });
        if (this.hiddenGridApi) {
          this.hiddenGridApi.applyColumnState({
            state: this.sortModel,
            applyOrder: false
          });
          setTimeout(() => this.extractFilteredDataFromHiddenGrid(), 0);
        }
      } catch (error) {
        console.warn('Failed to restore sort model:', error);
      }
    }
  }

  private applyFilterAndSortViaHiddenGrid(): void {
    if (!this.hiddenGridApi) {
      this.pendingFilterSort = true;
      return;
    }

    if (this.filterModel && Object.keys(this.filterModel).length > 0) {
      this.hiddenGridApi.setFilterModel(this.filterModel);
    } else {
      this.hiddenGridApi.setFilterModel(null);
    }

    if (this.sortModel?.length > 0) {
      this.hiddenGridApi.applyColumnState({
        state: this.sortModel,
        applyOrder: false
      });
    } else {
      this.hiddenGridApi.applyColumnState({ defaultState: { sort: null } });
    }

    setTimeout(() => this.extractFilteredDataFromHiddenGrid(), 0);
  }

  private extractFilteredDataFromHiddenGrid(): void {
    if (!this.hiddenGridApi) return;

    const filteredData: any[] = [];
    this.hiddenGridApi.forEachNodeAfterFilterAndSort((node) => {
      if (node.data) filteredData.push(node.data);
    });
    this.filteredRowData = filteredData;
  }

  ngOnDestroy(): void {
    this.gridApi = null as any;
    this.hiddenGridApi = null as any;
  }

  public getCurrentChartModel(): ChartModel | null {
    if (this.agGridChartComponent) {
      const model = this.agGridChartComponent.getChartModel();
      if (model) {
        this.lastKnownChartModel = model;
        return model;
      }
      const lastKnown = this.agGridChartComponent.getLastKnownChartModel();
      if (lastKnown) {
        this.lastKnownChartModel = lastKnown;
        return lastKnown;
      }
    }
    return this.lastKnownChartModel;
  }
}
